

import React, { useState, useRef, useEffect } from 'react';
import '../FileUpload.css';
import ClipLoader from 'react-spinners/ClipLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileAlt,
  faTimes,
  faCloudUploadAlt,
  faFolderOpen,
  faBriefcase,
  faCogs,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { backend_url } from '../config'; // Assuming backend_url is in config.js
import {
  experienceYearsRanges,
  allJobRolesData,
  sapModulesData,
} from './Constants'; // Import allJobRolesData and sapModulesData

// Function to generate comprehensive job description based on any job role
const generateJobDescription = (
  jobRoleName,
  skills = [],
  experienceLevel = ''
) => {
  if (!jobRoleName) return '';

  // Determine if it's an SAP-specific role for tailored description
  const isSapRole = Object.prototype.hasOwnProperty.call(
    sapModulesData,
    jobRoleName
  );

  let jobTitle = `${jobRoleName}`;
  let jobDescription = `Job Title: ${jobTitle}

Job Summary:
We are seeking an experienced ${jobRoleName} to join our dynamic team. The ideal candidate will have hands-on experience in relevant technologies and practices.

Key Responsibilities:
• Design, develop, and maintain solutions relevant to ${jobRoleName}
• Collaborate with cross-functional teams to define, design, and ship new features
• Ensure the performance, quality, and responsiveness of applications/systems
• Identify and correct bottlenecks and fix bugs
• Help maintain code quality, organization, and automation
• Participate in code reviews and contribute to team knowledge sharing
• Stay up-to-date with emerging technologies and industry trends
`;

  // Add experience level if selected
  if (experienceLevel) {
    jobDescription += `
Required Experience:
• ${experienceLevel} of hands-on experience in ${jobRoleName}
`;
  } else {
    jobDescription += `
Required Experience:
• Minimum 3+ years of hands-on experience in ${jobRoleName}
`;
  }

  // Add specific skills if selected or default skills from the job role
  if (skills.length > 0) {
    jobDescription += `
Required Skills and Technologies:
`;
    skills.forEach((skill) => {
      jobDescription += `• ${skill}\n`;
    });
  } else {
    const roleSkills = allJobRolesData[jobRoleName] || [];
    if (roleSkills.length > 0) {
      jobDescription += `
Required Skills and Technologies:
`;
      // Take first 10 skills as key requirements
      roleSkills.slice(0, 10).forEach((skill) => {
        jobDescription += `• ${skill}\n`;
      });
      if (roleSkills.length > 10) {
        jobDescription += `• And other relevant skills for a ${jobRoleName} role\n`;
      }
    }
  }

  // Add common requirements, with SAP specifics if applicable
  jobDescription += `

Additional Requirements:
• Bachelor's degree in Computer Science, Information Technology, or a related field (or equivalent practical experience)
• Strong analytical and problem-solving skills
• Excellent communication and interpersonal skills
• Ability to work independently and as part of a collaborative team
• Experience with Agile/Scrum methodologies
• Strong documentation and presentation skills
`;

  if (isSapRole) {
    jobDescription += `
• SAP certification in ${jobRoleName} (highly preferred)
• Experience with SAP implementation methodologies (SAP Activate, ASAP)
• Knowledge of integration with other SAP modules and third-party systems
• Experience in gathering business requirements and translating them into technical solutions
• Understanding of SAP security and authorization concepts
• Experience in multiple full-cycle SAP implementations
`;
  } else {
    jobDescription += `
• Experience with cloud platforms (AWS, Azure, GCP) (where applicable)
• Knowledge of continuous integration/continuous deployment (CI/CD) practices (where applicable)
`;
  }

  jobDescription += `
Preferred Qualifications:
• Relevant industry certifications
• Experience in a fast-paced, dynamic environment
• Previous experience in client-facing roles
• Strong project management and time management skills (for lead roles)

What We Offer:
• Competitive salary and comprehensive benefits package
• Professional development and learning opportunities
• Collaborative and innovative work environment
• Opportunity to work on challenging and impactful projects
• Career growth and advancement opportunities
• Flexible work arrangements and work-life balance
• Continuous learning and skill development programs
• Opportunity to work with global clients and diverse projects

Location: [Location to be specified]
Employment Type: Full-time
Experience Level: ${experienceLevel || 'Mid to Senior Level'}

We are an equal opportunity employer committed to diversity and inclusion.`;

  return jobDescription;
};

function FileUpload({ setAnalysisResults }) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [jobDescriptionFile, setJobDescriptionFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [selectedJobRole, setSelectedJobRole] = useState('');
  const [availableSkills, setAvailableSkills] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [selectedExperience, setSelectedExperience] = useState('');

  const [generatedTextPrefix, setGeneratedTextPrefix] = useState('');
  const [additionalUserText, setAdditionalUserText] = useState('');

  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const jobDescriptionFileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Populate available skills based on the selected job role from the combined data
    if (selectedJobRole && allJobRolesData[selectedJobRole]) {
      setAvailableSkills(allJobRolesData[selectedJobRole]);
      setSelectedSkills([]); // Reset selected skills when job role changes
    } else {
      setAvailableSkills([]);
      setSelectedSkills([]);
    }
  }, [selectedJobRole]);

  useEffect(() => {
    // If a job role is selected, generate the full job description
    if (selectedJobRole) {
      const fullJobDescription = generateJobDescription(
        selectedJobRole,
        selectedSkills,
        selectedExperience
      );
      setGeneratedTextPrefix(fullJobDescription);
      return;
    }

    // Original logic for manual selection (when no job role is selected)
    let prefix = '';
    if (selectedSkills.length > 0) {
      prefix += `Required Skills: ${selectedSkills.join(', ')}\n`;
    }
    if (selectedExperience) {
      prefix += `Required Experience: ${selectedExperience}\n`;
    }
    if (prefix) {
      prefix += '\n--- Additional Job Details (edit or add below) ---\n';
    }
    setGeneratedTextPrefix(prefix);
  }, [selectedJobRole, selectedSkills, selectedExperience]);

  const processFiles = (files) => {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
    ];
    const validFiles = files.filter((file) => allowedTypes.includes(file.type));
    const invalidFiles = files.filter(
      (file) => !allowedTypes.includes(file.type)
    );

    let errorMsg = '';
    if (invalidFiles.length > 0) {
      errorMsg = `Unsupported resume file type(s): ${invalidFiles
        .map((f) => f.name)
        .join(', ')}. Allowed: PDF, DOC, DOCX, TXT`;
    }
    setErrorMessage(errorMsg);

    if (errorMsg === '') {
      const newUniqueFiles = validFiles.filter(
        (vf) =>
          !selectedFiles.some(
            (sf) => sf.name === vf.name && sf.size === vf.size
          )
      );
      setSelectedFiles((prevFiles) => [...prevFiles, ...newUniqueFiles]);
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    processFiles(files);
    event.target.value = null; // Reset file input
  };

  const handleFolderChange = (event) => {
    const files = Array.from(event.target.files);
    processFiles(files);
    event.target.value = null; // Reset file input
  };

  const handleJobDescriptionFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedJdTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
    ];
    if (!allowedJdTypes.includes(file.type)) {
      setErrorMessage(
        `Unsupported job description file type: ${file.name}. Allowed: PDF, DOC, DOCX, TXT`
      );
      if (jobDescriptionFileInputRef.current) {
        jobDescriptionFileInputRef.current.value = '';
      }
      setJobDescriptionFile(null);
      return;
    }
    setErrorMessage('');
    setJobDescriptionFile(file);
    setSelectedJobRole(''); // Clear auto-generation fields when JD file is uploaded
    setSelectedSkills([]);
    setSelectedExperience('');
    setAdditionalUserText('');
    event.target.value = null; // Reset file input
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleRemoveJobDescriptionFile = () => {
    setJobDescriptionFile(null);
    if (jobDescriptionFileInputRef.current) {
      jobDescriptionFileInputRef.current.value = '';
    }
  };

  const handleJobRoleChange = (event) => {
    setSelectedJobRole(event.target.value);
    setAdditionalUserText(''); // Clear additional text when job role changes
    if (event.target.value) {
      setJobDescriptionFile(null); // Clear JD file if a job role is selected
      if (jobDescriptionFileInputRef.current)
        jobDescriptionFileInputRef.current.value = '';
    }
  };

  const handleSkillChange = (event) => {
    const { value, checked } = event.target;
    const newSkills = checked
      ? [...selectedSkills, value]
      : selectedSkills.filter((skill) => skill !== value);
    setSelectedSkills(newSkills);
    if (newSkills.length > 0 || checked) {
      setJobDescriptionFile(null); // Clear JD file if any skill is selected
      if (jobDescriptionFileInputRef.current)
        jobDescriptionFileInputRef.current.value = '';
    }
  };

  const handleExperienceChange = (event) => {
    setSelectedExperience(event.target.value);
    if (event.target.value) {
      setJobDescriptionFile(null); // Clear JD file if experience is selected
      if (jobDescriptionFileInputRef.current)
        jobDescriptionFileInputRef.current.value = '';
    }
  };

  const handleCombinedJobDescriptionChange = (event) => {
    const fullText = event.target.value;
    if (generatedTextPrefix && fullText.startsWith(generatedTextPrefix)) {
      setAdditionalUserText(fullText.substring(generatedTextPrefix.length));
    } else {
      setAdditionalUserText(fullText);
    }
    if (fullText.trim() !== '') {
      setJobDescriptionFile(null); // Clear JD file if user manually types
      if (jobDescriptionFileInputRef.current)
        jobDescriptionFileInputRef.current.value = '';
    }
  };

  const handleFileUpload = async () => {
    const completeJobDescriptionText = (
      generatedTextPrefix + additionalUserText
    ).trim();

    if (!selectedFiles.length) {
      setErrorMessage('Please upload resumes.');
      return;
    }

    const isJdProvided =
      selectedJobRole || // If job role is selected, we have auto-generated JD
      jobDescriptionFile ||
      completeJobDescriptionText.length > 50; // Minimum length check for manual input

    if (!isJdProvided) {
      setErrorMessage(
        'Please provide a job description (select a Job Role for auto-generation, type directly, or upload a file).'
      );
      return;
    }

    setErrorMessage('');
    setIsLoading(true);
    const formData = new FormData();
    selectedFiles.forEach((file) => {
      formData.append('resumes', file, file.name);
    });

    if (jobDescriptionFile) {
      formData.append(
        'jobDescriptionFile',
        jobDescriptionFile,
        jobDescriptionFile.name
      );
    } else if (completeJobDescriptionText) {
      formData.append('jobDescriptionText', completeJobDescriptionText);
    }

    try {
      const response = await fetch(`${backend_url}/upload`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setAnalysisResults(data);
        navigate('/results');
      } else {
        let errorResponseMessage = `Failed to upload files. Status: ${response.status}.`;
        try {
          const errorData = await response.json();
          errorResponseMessage = errorData.message || errorResponseMessage;
        } catch (jsonParseError) {
          try {
            const textError = await response.text();
            errorResponseMessage = textError || errorResponseMessage;
          } catch (textParseError) {
            /* Do nothing more */
          }
        }
        setErrorMessage(errorResponseMessage);
      }
    } catch (error) {
      setErrorMessage(`An error occurred: ${error.message || 'Network error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const jobDescriptionForTextarea = generatedTextPrefix + additionalUserText;

  const isAnalyzeButtonDisabled =
    isLoading ||
    !selectedFiles.length ||
    (!selectedJobRole && // If no job role selected, check other conditions
      !jobDescriptionFile &&
      jobDescriptionForTextarea.trim().length < 50);

  return (
    <div className='coreops-app-container'>
      <header className='coreops-main-header'>
        <h1>COREHire Talent Analyzer</h1>
        <p className='coreops-subtitle'>
          Streamline Your Hiring with Intelligent Resume Analysis.
        </p>
      </header>
      <main className='coreops-main-content'>
        <div className='coreops-file-upload-layout compact-layout'>
          <section className='coreops-upload-section resume-section'>
            <h2 className='coreops-section-title'>
              <FontAwesomeIcon icon={faUsers} /> Upload Candidate Resumes
            </h2>
            <div
              className='coreops-drag-drop-area'
              onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.classList.add('drag-over');
              }}
              onDragLeave={(e) => e.currentTarget.classList.remove('drag-over')}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('drag-over');
                const files = Array.from(e.dataTransfer.files);
                processFiles(files);
              }}
            >
              <FontAwesomeIcon
                icon={faCloudUploadAlt}
                className='coreops-upload-icon'
              />
              <p className='drag-drop-text'>Drag & Drop Resumes Here</p>
              <p className='drag-drop-subtext'>(PDF, DOC, DOCX, TXT)</p>
              <span className='drag-drop-or'>or</span>
              <div className='coreops-button-group'>
                <button
                  className='coreops-button coreops-button-secondary'
                  onClick={() =>
                    fileInputRef.current && fileInputRef.current.click()
                  }
                >
                  Browse Files
                </button>
                <button
                  className='coreops-button coreops-button-secondary'
                  onClick={() =>
                    folderInputRef.current && folderInputRef.current.click()
                  }
                >
                  <FontAwesomeIcon icon={faFolderOpen} /> Upload Folder
                </button>
              </div>
              <input
                type='file'
                multiple
                accept='.pdf,.doc,.docx,.txt'
                onChange={handleFileChange}
                style={{ display: 'none' }}
                ref={fileInputRef}
              />
              <input
                type='file'
                webkitdirectory='true'
                directory=''
                multiple
                onChange={handleFolderChange}
                style={{ display: 'none' }}
                ref={folderInputRef}
              />
            </div>

            {selectedFiles.length > 0 && (
              <div className='coreops-selected-files-container'>
                <h4 className='coreops-selected-files-title'>
                  Selected Resumes ({selectedFiles.length}):
                </h4>
                <div className='coreops-selected-files-list scrollable'>
                  {selectedFiles.map((file, index) => (
                    <div
                      className='coreops-file-item'
                      key={index + '-' + file.name + '-' + file.size}
                    >
                      <FontAwesomeIcon
                        icon={faFileAlt}
                        className='coreops-file-icon'
                      />
                      <span className='coreops-file-name' title={file.name}>
                        {file.name}
                      </span>
                      <button
                        type='button'
                        className='coreops-remove-file-button'
                        onClick={() => handleRemoveFile(index)}
                        title='Remove file'
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          <section className='coreops-upload-section job-description-section'>
            <h2 className='coreops-section-title'>
              <FontAwesomeIcon icon={faBriefcase} /> Provide Job Description
            </h2>

            <div className='coreops-structured-jd-helpers'>
              <h4 className='coreops-subsection-title'>
                Define with Job Role Specifics:
              </h4>
              <div className='coreops-jd-input-group'>
                <label htmlFor='job-role-select'>Target Job Role:</label>
                <select
                  id='job-role-select'
                  value={selectedJobRole}
                  onChange={handleJobRoleChange}
                  className='coreops-select'
                >
                  <option value=''>-- Select Job Role --</option>
                  {/* Now iterating over the combined allJobRolesData */}
                  {Object.keys(allJobRolesData).map((roleName) => (
                    <option key={roleName} value={roleName}>
                      {roleName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Removed the conditional rendering for the skills checkbox group.
                  The checkbox group will now only appear if NO job role is selected. */}
              {!selectedJobRole && availableSkills.length > 0 && (
                <div className='coreops-jd-input-group'>
                  <label>Key Skills:</label>
                  <div className='coreops-skills-checkbox-group scrollable'>
                    {availableSkills.map((skill) => (
                      <div key={skill} className='coreops-skill-checkbox-item'>
                        <input
                          type='checkbox'
                          id={`skill-${skill.replace(/\s+/g, '-')}`}
                          value={skill}
                          checked={selectedSkills.includes(skill)}
                          onChange={handleSkillChange}
                        />
                        <label htmlFor={`skill-${skill.replace(/\s+/g, '-')}`}>
                          {skill}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className='coreops-jd-input-group'>
                <label htmlFor='experience-select'>
                  Required Experience Level:
                </label>
                <select
                  id='experience-select'
                  value={selectedExperience}
                  onChange={handleExperienceChange}
                  className='coreops-select'
                >
                  <option value=''>-- Select Experience --</option>
                  {experienceYearsRanges.map((range) => (
                    <option key={range} value={range}>
                      {range}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className='coreops-jd-main-textbox-section'>
              <h4 className='coreops-subsection-title'>
                {selectedJobRole
                  ? 'Auto-Generated Job Description (Editable):'
                  : 'Or Enter/Paste Full Job Description:'}
              </h4>
              <textarea
                rows='15'
                value={jobDescriptionForTextarea}
                onChange={handleCombinedJobDescriptionChange}
                placeholder={
                  selectedJobRole
                    ? 'Auto-generated job description based on selected job role. You can edit or add more details below...'
                    : 'Select a Job Role above for auto-generated job description, or directly paste/write the full job description here...'
                }
                className='coreops-textarea'
              />
            </div>

            <div className='coreops-jd-separator'>
              <span>OR UPLOAD JD FILE</span>
            </div>

            <div className='coreops-jd-file-upload-area'>
              <div
                className='coreops-drag-drop-area coreops-drag-drop-area-small'
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.add('drag-over');
                }}
                onDragLeave={(e) =>
                  e.currentTarget.classList.remove('drag-over')
                }
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('drag-over');
                  if (e.dataTransfer.files.length > 0) {
                    const file = e.dataTransfer.files[0];
                    const allowedJdTypes = [
                      'application/pdf',
                      'application/msword',
                      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                      'text/plain',
                    ];
                    if (allowedJdTypes.includes(file.type)) {
                      const mockEvent = { target: { files: [file] } };
                      handleJobDescriptionFileChange(mockEvent);
                    } else {
                      setErrorMessage(
                        `Unsupported JD file type: ${file.name}. Allowed: PDF, DOC, DOCX, TXT`
                      );
                    }
                  }
                }}
              >
                <FontAwesomeIcon
                  icon={faCloudUploadAlt}
                  className='coreops-upload-icon small'
                />
                <p className='drag-drop-text small'>Drag & Drop JD File</p>
                <p className='drag-drop-subtext small'>(PDF, DOC, DOCX, TXT)</p>
                <button
                  className='coreops-button coreops-button-secondary coreops-button-small'
                  onClick={() =>
                    jobDescriptionFileInputRef.current &&
                    jobDescriptionFileInputRef.current.click()
                  }
                >
                  Browse JD File
                </button>
                <input
                  type='file'
                  accept='.pdf,.doc,.docx,.txt'
                  onChange={handleJobDescriptionFileChange}
                  style={{ display: 'none' }}
                  ref={jobDescriptionFileInputRef}
                />
              </div>
              {jobDescriptionFile && (
                <div className='coreops-selected-files-list single-file'>
                  <h4 className='coreops-selected-files-title'>
                    Selected JD File:
                  </h4>
                  <div className='coreops-file-item'>
                    <FontAwesomeIcon
                      icon={faFileAlt}
                      className='coreops-file-icon'
                    />
                    <span
                      className='coreops-file-name'
                      title={jobDescriptionFile.name}
                    >
                      {jobDescriptionFile.name}
                    </span>
                    <button
                      type='button'
                      className='coreops-remove-file-button'
                      onClick={handleRemoveJobDescriptionFile}
                      title='Remove JD file'
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </main>
      <div className='coreops-upload-analyze-section'>
        {errorMessage && (
          <div className='coreops-error-message'>{errorMessage}</div>
        )}
        <button
          className={`coreops-button coreops-button-primary analyze-button ${isLoading ? 'loading' : ''
            }`}
          onClick={handleFileUpload}
          disabled={isAnalyzeButtonDisabled}
          title={
            isAnalyzeButtonDisabled
              ? !selectedFiles.length
                ? 'Please upload resumes first'
                : 'Please provide a job description'
              : 'Analyze Resumes'
          }
        >
          {isLoading ? (
            <>
              <ClipLoader color='var(--coreops-light-text)' size={18} />
              <span>Analyzing...</span>
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faCogs} /> Analyze Resumes
            </>
          )}
        </button>
      </div>
      <footer className='coreops-footer'>
        <p>
          © {new Date().getFullYear()} COREHire - Intelligent Hiring Solutions
        </p>
      </footer>
    </div>
  );
}

export default FileUpload;
